import { MoveToInbox } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../../components/ui/customDataGrid/customDataGrid.component";
import {
  KSEF_INVOICE_TYPE_LABEL_KEYS,
  KSEF_INVOICE_TYPE_OPTIONS,
  PURCHASE_INVOICES_ENDPOINT,
} from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { formatNumber } from "../../utils";
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
  equalsStringFilterOperators,
  singleSelectFilterOperators,
} from "../../components/ui/customDataGrid/filterOperators";

export default function PurchaseInvoicesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewPurchaseInvoices = useHasPermission(
    "invoice.view_purchaseinvoice"
  );

  const INVOICE_TYPE_OPTIONS = useMemo(
    () => KSEF_INVOICE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: "invoice_number",
      headerName: t("Invoice number"),
      minWidth: 160,
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "invoice_type",
      headerName: t("Invoice kind"),
      minWidth: 150,
      sortable: false,
      type: "singleSelect",
      valueOptions: INVOICE_TYPE_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.invoice_type,
      renderCell: (params) =>
        t(KSEF_INVOICE_TYPE_LABEL_KEYS[params.row.invoice_type] ?? params.row.invoice_type),
    },
    {
      field: "seller_name",
      headerName: t("Seller"),
      minWidth: 220,
      flex: 1,
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "seller_nip",
      headerName: "NIP",
      minWidth: 110,
      sortable: false,
      filterOperators: equalsStringFilterOperators(),
    },
    {
      field: "issued_at",
      headerName: t("Date of issue"),
      minWidth: 120,
      filterOperators: dateRangeFilterOperators(),
    },
    {
      field: "net_amount",
      headerName: t("Net value"),
      minWidth: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value, row) => `${formatNumber(value)} ${row.currency}`,
    },
    {
      field: "gross_amount",
      headerName: t("Gross value"),
      minWidth: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value, row) => `${formatNumber(value)} ${row.currency}`,
    },
    {
      field: "ksef_number",
      headerName: t("KSeF number"),
      minWidth: 300,
      sortable: false,
      filterOperators: containsStringFilterOperators(),
    },
  ];

  if (!canViewPurchaseInvoices) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<MoveToInbox />}
      title={t("Purchase invoices list")}
      url={PURCHASE_INVOICES_ENDPOINT}
      gridSettingsKey="PURCHASE_INVOICES"
      columns={columns}
      defaultHiddenFields={["seller_nip", "ksef_number"]}
      refreshEventName="purchaseInvoicesDownloaded"
      onRowClick={(id) => navigate(`/invoices/purchase/${id}`)}
    />
  );
}
